"use client";

import { useEffect, useState } from "react";
import { validatePassword } from "@/lib/password-validation";
import { cn } from "@/lib/utils";

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

export function PasswordStrengthIndicator({
  password,
  className,
}: PasswordStrengthIndicatorProps) {
  const [validation, setValidation] = useState<{
    strength: "weak" | "medium" | "strong";
    errors: string[];
  }>({
    strength: "weak",
    errors: [],
  });

  useEffect(() => {
    if (!password) {
      setValidation({ strength: "weak", errors: [] });
      return;
    }

    const result = validatePassword(password);
    setValidation({
      strength: result.strength,
      errors: result.errors,
    });
  }, [password]);

  if (!password) return null;

  const strengthConfig = {
    weak: {
      color: "bg-red-500",
      width: "w-1/3",
      text: "Weak",
      textColor: "text-red-500",
    },
    medium: {
      color: "bg-yellow-500",
      width: "w-2/3",
      text: "Medium",
      textColor: "text-yellow-500",
    },
    strong: {
      color: "bg-green-500",
      width: "w-full",
      text: "Strong",
      textColor: "text-green-500",
    },
  };

  const config = strengthConfig[validation.strength];

  return (
    <div className={cn("space-y-2", className)}>
      <div className='flex items-center justify-between text-sm'>
        <span className='text-muted-foreground'>Password strength:</span>
        <span className={cn("font-medium", config.textColor)}>
          {config.text}
        </span>
      </div>
      <div className='h-2 w-full bg-muted rounded-full overflow-hidden'>
        <div
          className={cn(
            "h-full transition-all duration-300",
            config.color,
            config.width
          )}
        />
      </div>
      {validation.errors.length > 0 && (
        <ul className='text-xs text-red-500 space-y-1'>
          {validation.errors.map((error, index) => (
            <li key={index} className='flex items-start'>
              <span className='mr-1'>•</span>
              <span>{error}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
